import { useConfigStore } from '@/stores';
import styles from './styles.module.scss';
import { useShopStore } from '@/stores';
import ShopFnbFacilityCategoryButton from '@/components/common/shop-fnb-facility/buttons/category';
import { useShopBuildingOptions, useShopCategories } from '@/hooks/poi/useShopData';
import type { BuildingCategory } from '@/services/dto';
import { useTranslation } from 'react-i18next';
import { SHOP_SEARCH_MODE } from '@/enums/shop';
import { useOrientation } from '@/hooks';
import { useClippedFocusableIndices } from '@/hooks/useClippedFocusableIndices';
import { getCategoryLocalizedName } from '@/utils/poi';
import clsx from 'clsx';
import { useRef } from 'react';

const getCategoryLabel = (category: BuildingCategory, language: string, t: (key: string) => string): string => {
    if (category.clientCode === 'all') {
        const name = category.name;
        if (typeof name === 'string') return t(name);
        if (Array.isArray(name)) return getCategoryLocalizedName(category, language);
        return t(name[language]);
    }
    return getCategoryLocalizedName(category, language);
};

interface ShopSearchByCategoryProps {
    onSelectCategory?: (category: BuildingCategory) => void;
}

const ShopSearchByCategory = ({ onSelectCategory }: ShopSearchByCategoryProps) => {
    const language = useConfigStore((state) => state.config.ui.language);
    const { isHorizontal } = useOrientation();
    const screenMode = useConfigStore((s) => s.config?.ui?.screenMode);
    const { t } = useTranslation();
    const selectedCategory = useShopStore((state) => state.selectedCategory);
    const setSelectedCategory = useShopStore((state) => state.setSelectedCategory);
    const updateShopContents = useShopStore((state) => state.updateShopContents);
    const buildingOptions = useShopBuildingOptions(t);
    const shop = useShopCategories();
    const containerRef = useRef<HTMLDivElement>(null);
    const focusableIndices = useClippedFocusableIndices(containerRef, shop.length);

    const handleSelectCategory = (category: BuildingCategory) => {
        setSelectedCategory(category);
        onSelectCategory?.(category);
        updateShopContents(SHOP_SEARCH_MODE.SEARCH_BY_CATEGORY);
    };

    return (
        <div
            ref={containerRef}
            data-region-name="shop-search-by-category"
            className={clsx(styles['shop-search-by-category'], {
                [styles['shop-search-by-category--single-building']]: buildingOptions.length === 1,
                [styles['shop-search-by-category--vertical']]: !isHorizontal,
                [styles['shop-search-by-category--vertical--halfmode']]: !isHorizontal && screenMode === 'split',
            })}
            data-region-nav
        >
            {shop.map((category, index) => {
                const isFocusable = focusableIndices === null || focusableIndices.has(index);

                return (
                    <ShopFnbFacilityCategoryButton
                        tabIndex={isFocusable ? 9 : -1}
                        data-interactive={isFocusable ? true : undefined}
                        role="radio"
                        type="button"
                        aria-hidden={!isFocusable}
                        aria-checked={selectedCategory?.clientCode === category.clientCode}
                        aria-label={getCategoryLabel(category, language, t)}
                        key={category.clientCode}
                        onClick={() => handleSelectCategory(category)}
                        title={getCategoryLabel(category, language, t)}
                    />
                );
            })}
        </div>
    );
};

export default ShopSearchByCategory;
